import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { TypeOf, z } from "zod";
import { Profile } from "../../../../../@types/profile";
import DelendCentralizedCircularProgress from "../../../../../components/CircularProgress/DelendCentralizedCircularProgress";
import { MobilePhoneMask } from "../../../../../components/Mask/DelendTextMask";
import { useAppDispatch, useAppSelector } from "../../../../../hooks/hooks";
import ProfileService from "../../../../../services/ProfileService";
import { onlyNumbers } from "../../../../../services/UtilsService";
import { add } from "../../../../../store/profileSlice";
import ReturnButton from "../../ReturnButton";

const profileMobilePhoneInput = z.object({
  mobilePhone: z
    .string()
    .nonempty({ message: "celular é obrigatório" })
    .min(15, { message: "celular inválido" })
    .transform((mobilePhone) => "55" + onlyNumbers(mobilePhone)),
});

type ProfileNameInput = TypeOf<typeof profileMobilePhoneInput>;

const ProfileHeader = () => {
  return (
    <Box display={"flex"} flexDirection={"column"} gap={1}>
      <Typography variant="h2">Celular de contato</Typography>

      <Typography>
        Este número será utilizado para contato via whatsapp e/ou SMS.
      </Typography>
    </Box>
  );
};

const ProfileMobilePhoneEditor: React.FC = () => {
  const navigate = useNavigate();

  const profile = useAppSelector<Profile | undefined>(
    (state) => state.profileSlice.profile
  );

  const dispatch = useAppDispatch();

  const [isButtonDisabled, setIsButtonDisabled] = useState(true);

  const {
    register,
    clearErrors,
    setError,
    formState: { errors, isSubmitting },
    handleSubmit,
  } = useForm<ProfileNameInput>({
    resolver: zodResolver(profileMobilePhoneInput),
  });

  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = `55${onlyNumbers(event.target.value)}`;
    clearErrors("mobilePhone");
    setIsButtonDisabled(newValue === profile?.person.mobilePhone);
  };

  if (!profile) return <DelendCentralizedCircularProgress />;

  const onSubmit: SubmitHandler<ProfileNameInput> = async (
    profileMobilePhoneData
  ) => {
    try {
      await ProfileService.updateProfile(
        profileMobilePhoneData,
        profile.person.uuid
      );
      const profileUpdated = await ProfileService.getProfile();
      dispatch(add(profileUpdated));

      toast.success("Alterações salvas com sucesso!");
      navigate("/dashboard/profile");
    } catch (error: any) {
      if (
        error.response.status === 400 &&
        error.response.data.message === "O telefone informado já está em uso"
      ) {
        setError("mobilePhone", {
          type: "manual",
          message:
            "este celular já está sendo utilizado por outra conta, por favor utilize um número diferente",
        });
        return;
      }
      toast.error("Erro ao salvar alterações");
    }
  };

  return (
    <Box display={"flex"} gap={2}>
      <ReturnButton to="/dashboard/profile" />
      <Box
        display={"flex"}
        flexDirection={"column"}
        gap={2}
        component="form"
        noValidate
        autoComplete="off"
        onSubmit={handleSubmit(onSubmit)}
        width={"100%"}
      >
        <ProfileHeader />
        <TextField
          {...register("mobilePhone")}
          error={!!errors.mobilePhone}
          onChange={handleTextChange}
          helperText={errors.mobilePhone?.message}
          defaultValue={profile.person.mobilePhone.slice(2)}
          variant="standard"
          fullWidth
          label="celular com DDD"
          disabled={isSubmitting}
          InputProps={{ inputComponent: MobilePhoneMask as any }}
        />
        <Box display={"flex"} justifyContent={"end"}>
          <Button
            variant="outlined"
            size="large"
            disabled={isButtonDisabled || isSubmitting}
            type="submit"
            endIcon={isSubmitting && <CircularProgress size={20} />}
          >
            salvar
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ProfileMobilePhoneEditor;
